/**
 * Currency display for product prices (Fair Product card: `39$`).
 * Maps ISO codes + already-symbolic values to a display glyph.
 */

const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  RUB: '₽',
  BYN: 'Br',
}

/** True when value is already a glyph (`$`, `€`, `₽`), not an ISO code. */
function isCurrencyGlyph(value: string): boolean {
  if (!value) return false
  if (/^[A-Za-z]{3}$/.test(value)) return false
  return value.length <= 2 || value.startsWith('$') || value.startsWith('€')
}

/**
 * Resolve API `currency` (ISO code or glyph) to a display symbol.
 * Falls back to Intl narrowSymbol for unknown ISO codes.
 */
export function getCurrencySymbol(currency: string): string {
  const raw = currency?.trim() ?? ''
  if (!raw) return ''

  if (isCurrencyGlyph(raw)) {
    return raw
  }

  const code = raw.toUpperCase()
  if (CURRENCY_SYMBOLS[code]) {
    return CURRENCY_SYMBOLS[code]
  }

  try {
    const parts = new Intl.NumberFormat('en', {
      style: 'currency',
      currency: code,
      currencyDisplay: 'narrowSymbol',
    }).formatToParts(0)
    return parts.find((part) => part.type === 'currency')?.value ?? raw
  } catch {
    return raw
  }
}

/**
 * Format amount + currency for Fair cards.
 * Single-char symbols → suffix no space (`313.45$`, `390₽`).
 * Multi-char (`Br`) → suffix with space (`120 Br`).
 */
export function formatPriceWithCurrency(
  amount: number,
  currency: string,
): string {
  const symbol = getCurrencySymbol(currency)
  // Avoid float artifacts like `6391.4800000000005$` shifting card layout.
  const formatted = Number.isFinite(amount)
    ? Number(amount.toFixed(2)).toString()
    : String(amount)
  if (!symbol) {
    return formatted
  }
  if (symbol.length === 1) {
    return `${formatted}${symbol}`
  }
  return `${formatted} ${symbol}`
}

/** The currency every request asks for (the `Currency` header the interceptor sends). */
export function readUserCurrency(): string {
  try {
    return (window.localStorage.getItem('currency') ?? 'USD').toUpperCase()
  } catch {
    return 'USD'
  }
}

/**
 * A card price: the symbol form when the amount is in the shopper's own
 * currency, the ISO code in front ("EUR 1333.63") when the server could not
 * convert it — a foreign amount must never read as the shopper's money.
 */
export function formatCardPrice(amount: number, currency: string, userCurrency = readUserCurrency()): string {
  const code = currency?.trim().toUpperCase() ?? ''
  if (code && /^[A-Z]{3}$/.test(code) && code !== userCurrency) {
    const formatted = Number.isFinite(amount) ? Number(amount.toFixed(2)).toString() : String(amount)
    return `${code}\u00a0${formatted}`
  }
  return formatPriceWithCurrency(amount, currency)
}
